import React, { useState } from 'react';
import type { ExternalPartner } from '../../../lib/types';
import { PartnerCommissionsSection } from './PartnerCommissionsSection';
import { PartnerProjectsPortalSection } from './PartnerProjectsPortalSection';
import { PartnerPortalSection } from './PartnerPortalSection';

export interface PartnerInfoTabProps {
  partner: ExternalPartner;
}

const Divider = (): JSX.Element => (
  <div className="h-px bg-[rgba(55,53,47,0.09)] dark:bg-[#3F4447] mx-2 my-1" />
);

/**
 * Contenitore del tab "Info" del drawer partner (mockup approvato: colonne "Dopo").
 * Sostituisce lo stack di tre box colorati (PartnerReferrerManager +
 * CollaboratorPortalAccessManager + PartnerPortalAccessManager) con sezioni a toggle
 * in stile Notion, tutte chiuse di default.
 *
 * Task 1: costruisce il contenitore + la sezione "Provvigioni" (PartnerCommissionsSection).
 * Task 2: sostituisce CollaboratorPortalAccessManager con PartnerProjectsPortalSection.
 * Task 3: sostituisce PartnerPortalAccessManager con PartnerPortalSection (il vecchio
 * manager resta nel codebase, non più montato qui: lo elimina il Task 4).
 */
export function PartnerInfoTab({ partner }: PartnerInfoTabProps): JSX.Element {
  const [openSection, setOpenSection] = useState<'commissions' | 'projects-portal' | 'partner-portal' | null>(null);
  // Le tre sezioni caricano solo su mount/partner.id: senza un segnale condiviso, un'azione
  // in una sezione (es. creare l'accesso "Portale progetti") lascia le altre due con dati
  // stale — in particolare "Portale partner" e "Portale progetti" leggono la stessa tabella
  // (vedi portalAccessKind.ts) e devono restare coerenti fra loro. `reloadToken` cambia ad
  // ogni mutazione riuscita in una qualsiasi sezione e le altre lo includono nei deps del
  // proprio effetto di caricamento per ricaricarsi di conseguenza.
  const [reloadToken, setReloadToken] = useState(0);
  const bumpReloadToken = () => setReloadToken((t) => t + 1);

  const toggle = (section: 'commissions' | 'projects-portal' | 'partner-portal') => {
    setOpenSection((prev) => (prev === section ? null : section));
  };

  return (
    <div className="px-2.5 pb-3 pt-2 flex flex-col [&_[role=button]>span:first-of-type]:whitespace-nowrap">
      <PartnerCommissionsSection
        partner={partner}
        open={openSection === 'commissions'}
        onToggle={() => toggle('commissions')}
        reloadToken={reloadToken}
        onDataChanged={bumpReloadToken}
      />

      <Divider />

      <PartnerProjectsPortalSection
        partner={partner}
        open={openSection === 'projects-portal'}
        onToggle={() => toggle('projects-portal')}
        reloadToken={reloadToken}
        onDataChanged={bumpReloadToken}
      />

      <Divider />

      <PartnerPortalSection
        partner={partner}
        open={openSection === 'partner-portal'}
        onToggle={() => toggle('partner-portal')}
        reloadToken={reloadToken}
        onDataChanged={bumpReloadToken}
      />
    </div>
  );
}
